import { type GroundStats, scoreGround } from "../../shared/ground";
import { startRound, submitScore } from "../api";
import type { Screen } from "../air/screen";
import { $, getCallsign, showRoundEnd } from "../ui";
import { GraphEditor } from "./editor";
import { GroundGame, isSensorKind, type PlaceKind } from "./game";
import { FlowEngine } from "./graph";
import { SELL_REFUND, SENSORS, WEAPONS } from "./world";

const specOf = (k: PlaceKind) => (isSensorKind(k) ? SENSORS[k] : WEAPONS[k]);

export function createGroundScreen(): Screen {
  const hudName = $("#g-hud-name");
  const hudWave = $("#g-hud-wave");
  const hudHp = $("#g-hud-hp");
  const hudCredits = $("#g-hud-credits");
  const hudScore = $("#g-hud-score");
  const startBtn = $<HTMLButtonElement>("#g-start");
  const message = $("#g-message");
  const sensorBar = $("#g-sensors");
  const weaponBar = $("#g-weapons");
  const pop = $("#g-sensor-pop");
  let roundId: string | null = null;
  let messageTimer = 0;

  const editor = new GraphEditor($("#g-graph"));
  const flow = new FlowEngine(() => editor.graph);

  const say = (text: string) => {
    message.textContent = text;
    message.classList.remove("phase-new");
    void message.offsetWidth;
    message.classList.add("phase-new");
    message.hidden = false;
    clearTimeout(messageTimer);
    messageTimer = window.setTimeout(() => (message.hidden = true), 4500);
  };

  const buyButtons = new Map<PlaceKind, HTMLButtonElement>();
  const kinds: [PlaceKind, HTMLElement][] = [
    ["acoustic", sensorBar],
    ["camera", sensorBar],
    ["drone", sensorBar],
    ["mortar", weaponBar],
    ["howitzer", weaponBar],
    ["attackDrone", weaponBar],
  ];
  for (const [kind, bar] of kinds) {
    const s = specOf(kind);
    const b = document.createElement("button");
    b.type = "button";
    b.className = `sensor-btn kind-${kind}`;
    b.innerHTML = `<span class="sensor-dot"></span>${s.label}<span class="sensor-cost">${s.cost}</span>`;
    b.title = s.blurb;
    b.addEventListener("click", () => {
      game.setPlacing(game.placingKind === kind ? null : kind);
      syncToolbar();
      if (game.placingKind) say(`Click the map to place: ${s.label.toLowerCase()}. ${s.blurb}`);
    });
    buyButtons.set(kind, b);
    bar.append(b);
  }

  const syncToolbar = () => {
    for (const [kind, b] of buyButtons) {
      b.classList.toggle("active", game.placingKind === kind);
      b.disabled = game.credit < specOf(kind).cost && game.placingKind !== kind;
    }
  };

  const game = new GroundGame($<HTMLCanvasElement>("#g-map"), flow, {
    onHud: (h) => {
      hudWave.textContent = h.wave ? String(h.wave) : "–";
      hudHp.textContent = String(h.hp);
      hudHp.classList.toggle("low", h.hp <= 6);
      if (h.credits > Number(hudCredits.textContent)) {
        hudCredits.classList.remove("gain");
        void hudCredits.offsetWidth;
        hudCredits.classList.add("gain");
      }
      hudCredits.textContent = String(h.credits);
      hudScore.textContent = String(h.score);
      if (h.phase === "break") startBtn.textContent = `Next wave in ${Math.ceil(h.breakLeft)}s`;
      else if (h.phase === "wave") startBtn.textContent = `Wave ${h.wave} in progress`;
      syncToolbar();
    },
    onFlow: (ev) => editor.flash(ev),
    onPlaced: (item) => {
      if (isSensorKind(item.kind)) editor.addSensor(item.id, item.kind, item.label);
      else editor.addGun(item.id, item.kind, item.label);
      syncToolbar();
    },
    onRemoved: (id) => editor.removeNode(id),
    onSelect: (s, screen) => {
      if (!s || !screen) {
        pop.hidden = true;
        return;
      }
      const host = pop.offsetParent?.getBoundingClientRect();
      pop.style.left = `${screen.x - (host?.left ?? 0) + 12}px`;
      pop.style.top = `${screen.y - (host?.top ?? 0) - 12}px`;
      $("#g-pop-name").textContent = s.label;
      $<HTMLButtonElement>("#g-pop-rotate").hidden = s.kind !== "camera";
      $("#g-pop-sell").textContent = `Sell (+${Math.floor(specOf(s.kind).cost * SELL_REFUND)})`;
      pop.hidden = false;
    },
    onMessage: say,
    onGameOver: (stats) => void finish(stats),
  });

  // Block load meters.
  window.setInterval(() => {
    for (const id of editor.blockIds) {
      const { load, tracks, cooldown } = flow.load(id, game.now);
      editor.setLoad(id, load, tracks, cooldown);
    }
  }, 100);

  $("#g-pop-rotate").addEventListener("click", () => game.rotateSelected());
  $("#g-pop-sell").addEventListener("click", () => game.sellSelected());
  window.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && game.placingKind) {
      game.setPlacing(null);
      syncToolbar();
    }
  });

  const reset = () => {
    game.reset();
    editor.reset();
    startBtn.disabled = false;
    startBtn.textContent = "Start wave 1";
    say("Place sensors on the map, wire them to the guns below, then start wave 1.");
  };

  startBtn.addEventListener("click", async () => {
    const name = getCallsign();
    if (!name || game.started) return;
    startBtn.disabled = true;
    roundId = null;
    try {
      roundId = (await startRound(name, "ground")).roundId;
    } catch (err) {
      say(`Offline game: your score won't reach the leaderboard (${(err as Error).message}).`);
    }
    game.start();
  });

  function finish(stats: GroundStats): Promise<void> {
    const score = scoreGround(stats);
    const id = roundId;
    return showRoundEnd({
      title: "Base overrun",
      score,
      rows: [
        ["Waves cleared", stats.wavesCleared],
        ["Infantry", stats.infantryKilled],
        ["Vehicles", stats.vehiclesKilled],
        ["Tanks", stats.armorKilled],
        ["Shells fired", stats.shellsFired],
      ],
      submit: id ? () => submitScore(id, score, stats) : null,
      onClose: reset,
    });
  }

  let initialised = false;
  return {
    show() {
      hudName.textContent = getCallsign() ?? "";
      if (!initialised) {
        initialised = true;
        reset();
      }
    },
  };
}
